import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Loader2, Sparkles } from 'lucide-react'
import { CreateStrengthRequest, IDPStrength, IDPItemStatus } from '@/services/api/idpService'
import { KPASelector, KPAOption } from './KPASelector'

interface StrengthDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (data: CreateStrengthRequest) => Promise<void>
  idpId: string
  userId: string | number
  year: number
  editingStrength?: IDPStrength | null
  kpas?: KPAOption[]  // KPAs from user's objectives
}

export function StrengthDialog({
  open,
  onOpenChange,
  onSubmit,
  idpId,
  userId,
  year,
  editingStrength = null,
  kpas
}: StrengthDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [formData, setFormData] = useState<CreateStrengthRequest>({
    title: '',
    description: '',
    linked_kpa_ids: [],
    supporting_evidence_ids: [],
    employee_notes: '',
    item_status: IDPItemStatus.EMPLOYEE_PROPOSED,
  })

  // Load data when editing
  useEffect(() => {
    if (editingStrength) {
      if (import.meta.env.DEV) console.log('🔍 Editing strength:', editingStrength.title)
      console.log('📎 Linked KPI IDs:', editingStrength.linked_kpa_ids)
      setFormData({
        title: editingStrength.title,
        description: editingStrength.description || '',
        linked_kpa_ids: editingStrength.linked_kpa_ids || [],
        supporting_evidence_ids: editingStrength.supporting_evidence_ids || [],
        employee_notes: editingStrength.employee_notes || '',
        item_status: editingStrength.item_status,
      })
    } else {
      setFormData({
        title: '',
        description: '',
        linked_kpa_ids: [],
        supporting_evidence_ids: [],
        employee_notes: '',
        item_status: IDPItemStatus.EMPLOYEE_PROPOSED,
      })
    }
  }, [editingStrength, open])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.title.trim() || !formData.description?.trim()) return

    setIsSubmitting(true)
    try {
      await onSubmit(formData)
      onOpenChange(false)
    } catch (error) {
      console.error('Failed to save strength:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleAddKPI = (kpiId: string) => {
    setFormData({
      ...formData,
      linked_kpa_ids: [...(formData.linked_kpa_ids || []), kpiId],
    })
  }

  const handleRemoveKPI = (kpiId: string) => {
    setFormData({
      ...formData,
      linked_kpa_ids: (formData.linked_kpa_ids || []).filter((id: string) => id !== kpiId),
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-blue-500" />
            {editingStrength ? 'Edit Strength' : 'Add Strength'}
          </DialogTitle>
          <DialogDescription>
            {editingStrength 
              ? 'Update this key strength that contributes to your success and impact & growth.'
              : 'Document a key strength that contributes to your success and impact & growth.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="title">
              Strength Title <span className="text-red-500">*</span>
            </Label>
            <Input
              id="title"
              value={formData.title}
              onChange={e => setFormData({ ...formData, title: e.target.value })}
              placeholder="My greatest skill that I use to impact students, colleagues and Eduvos…"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">
              Description <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={e => setFormData({ ...formData, description: e.target.value })}
              placeholder="My biggest, most proud and unique ability that I use to make an impact…"
              rows={4}
              required
            />
          </div>

          {/* KPA Selector - Only render when dialog is open and KPAs are available */}
          {open && kpas && kpas.length > 0 && (
            <KPASelector
              selectedKPAIds={formData.linked_kpa_ids || []}
              onAdd={handleAddKPI}
              onRemove={handleRemoveKPI}
              label="Linked KPAs"
              kpas={kpas}
            />
          )}

          <div className="space-y-2">
            <Label htmlFor="notes">Personal Notes</Label>
            <Textarea
              id="notes"
              value={formData.employee_notes}
              onChange={e => setFormData({ ...formData, employee_notes: e.target.value })}
              placeholder="Add any personal notes or context..."
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || !formData.title.trim() || !formData.description?.trim()}
            >
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {editingStrength ? 'Update Strength' : 'Add Strength'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
